import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Tabs,
  Tab,
  Checkbox,
  Grid,
  CircularProgress
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import BackupIcon from '@mui/icons-material/Backup';
import RestoreIcon from '@mui/icons-material/Restore';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SecurityIcon from '@mui/icons-material/Security';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import BusinessIcon from '@mui/icons-material/Business';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DescriptionIcon from '@mui/icons-material/Description';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EmailIcon from '@mui/icons-material/Email';
import SettingsIcon from '@mui/icons-material/Settings';
import CampaignIcon from '@mui/icons-material/Campaign';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { Navigate, useNavigate } from 'react-router-dom';
import API_CONFIG from '../config/api';
import { apiFetch } from '../api/client';
import { setUnitStorageJSON, withUnidade } from '../utils/storageByUnit';
import GavConfigPanel from '../components/config/GavConfigPanel';
import PortalEditaisDocsConfigPanel from '../components/config/PortalEditaisDocsConfigPanel';
import FluxosConfigPanel from '../components/contratos/FluxosConfigPanel';
import { usePrestadorAuth } from '../contexts/PrestadorAuthContext';

const BACKEND_URL = API_CONFIG.BACKEND_URL;

function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 3 }}>{children}</Box>;
}

function diasParaVencimento(dataFim) {
  if (!dataFim) return null;
  let vencimento;
  if (String(dataFim).includes('/') && String(dataFim).split('/').length === 3) {
    const [day, month, year] = dataFim.split('/');
    vencimento = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
  } else {
    vencimento = new Date(dataFim);
  }
  if (isNaN(vencimento.getTime())) return null;
  return Math.ceil((vencimento - new Date()) / (1000 * 60 * 60 * 24));
}

const EMAIL_TEMPLATES = {
  nf: (empresa) =>
    `Prezados,\n\nSolicitamos o envio da Nota Fiscal referente ao mês atual do contrato com a empresa ${empresa}.\n\nPedimos a gentileza de anexar a documentação completa no Portal do Prestador ou responder a este e-mail.\n\nAtenciosamente,\nSetor de Contratos`,
  renovacao: (empresa) =>
    `Prezados,\n\nInformamos que o contrato da empresa ${empresa} está próximo do vencimento.\n\nSolicitamos análise quanto à necessidade de renovação/aditivo e o envio da documentação pertinente.\n\nAtenciosamente,\nSetor de Contratos`
};

export default function ConfiguracaoPage() {
  const navigate = useNavigate();
  const { setPrestadorSession } = usePrestadorAuth();
  const {
    user,
    rbacRole,
    isAdmin,
    unidadeSelecionada,
    canAccessConfiguracao,
    isGlobalUser,
    getUserRole
  } = useAuth();

  const role = rbacRole || getUserRole?.() || null;
  const isSuperAdmin = role === ROLES.SUPER_ADMIN;
  const isLocalAdmin = role === ROLES.LOCAL_ADMIN || isAdmin;

  const [tab, setTab] = useState(0);
  const [config, setConfig] = useState({
    backendUrl: BACKEND_URL,
    alertDays: 30,
    emailNotifications: true,
    darkMode: false,
    compactMode: false
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [usuarios, setUsuarios] = useState([]);
  const [setores, setSetores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState({});

  const [listaUnidades, setListaUnidades] = useState([]);
  const [modalUnidade, setModalUnidade] = useState({ open: false, mode: 'create', unidade: null });
  const [formUnidade, setFormUnidade] = useState({ nome: '', codigo: '', cnpj: '' });
  const [formUnidadeLocal, setFormUnidadeLocal] = useState({ nome: '', codigo: '', cnpj: '' });

  // Central de Comunicações
  const [contratosComm, setContratosComm] = useState([]);
  const [fiscaisComm, setFiscaisComm] = useState([]);
  const [filtroSetor, setFiltroSetor] = useState('');
  const [filtroFiscal, setFiltroFiscal] = useState('');
  const [filtroVencimento, setFiltroVencimento] = useState('todos');
  const [loadingComm, setLoadingComm] = useState(false);
  const [emailModal, setEmailModal] = useState({
    open: false,
    tipo: null,
    contratos: [],
    corpo: '',
    copiaContratos: true,
    enviando: false
  });
  const [selectedIds, setSelectedIds] = useState([]);

  // Portal / God Mode
  const [prestadoresPortal, setPrestadoresPortal] = useState([]);
  const [emailEditModal, setEmailEditModal] = useState({ open: false, prestador: null, email: '' });
  const [confirmInativarPrest, setConfirmInativarPrest] = useState(null);
  const [filtroSetorPortal, setFiltroSetorPortal] = useState('');
  const [filtroFiscalPortal, setFiltroFiscalPortal] = useState('');
  const [filtroVencimentoPortal, setFiltroVencimentoPortal] = useState('todos');

  useEffect(() => {
    if (canAccessConfiguracao()) {
      fetchData();
      fetchUnidades();
      fetchComunicacoes();
      fetchPrestadoresPortal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, unidadeSelecionada, rbacRole]);

  useEffect(() => {
    if (unidadeSelecionada) {
      setFormUnidadeLocal({
        nome: unidadeSelecionada.nome || '',
        codigo: unidadeSelecionada.codigo || '',
        cnpj: unidadeSelecionada.cnpj || ''
      });
    }
  }, [unidadeSelecionada]);

  const fetchUnidades = async () => {
    try {
      const res = await apiFetch('/api/unidades');
      if (!res.ok) throw new Error('Erro ao buscar unidades');
      const data = await res.json();
      setListaUnidades(data);
    } catch (error) {
      console.error('Erro ao carregar unidades:', error);
      setSnackbar({ open: true, message: 'Erro ao carregar unidades', severity: 'error' });
    }
  };

  const fetchComunicacoes = useCallback(async () => {
    if (!unidadeSelecionada?.codigo) return;
    setLoadingComm(true);
    try {
      const [rPrest, rFisc, rSet] = await Promise.all([
        apiFetch(withUnidade('/api/prestadores', unidadeSelecionada)),
        apiFetch(withUnidade('/api/fiscais', unidadeSelecionada)),
        apiFetch(withUnidade('/api/setores', unidadeSelecionada))
      ]);
      if (rPrest.ok) {
        const data = await rPrest.json();
        setContratosComm(Array.isArray(data) ? data.filter((p) => !p.finalizado) : []);
      }
      if (rFisc.ok) {
        const data = await rFisc.json();
        setFiscaisComm(Array.isArray(data) ? data : []);
      }
      if (rSet.ok) {
        const data = await rSet.json();
        const nomes = (data || [])
          .filter((s) => s.ativo === undefined || s.ativo === 1)
          .map((s) => s.nome)
          .filter(Boolean);
        if (nomes.length) setSetores((prev) => Array.from(new Set([...prev, ...nomes])));
      }
    } catch (e) {
      console.warn('[Comunicacoes]', e);
    } finally {
      setLoadingComm(false);
    }
  }, [unidadeSelecionada]);

  const fetchPrestadoresPortal = async () => {
    if (!unidadeSelecionada?.codigo) return;
    try {
      const res = await apiFetch(withUnidade('/api/prestadores', unidadeSelecionada));
      if (res.ok) {
        const data = await res.json();
        setPrestadoresPortal((data || []).filter((p) => !p.finalizado));
      }
    } catch (e) {
      console.warn('[Portal]', e);
    }
  };

  const handleOpenModalUnidade = (mode = 'create', unidade = null) => {
    if (mode === 'create') {
      setFormUnidade({ nome: '', codigo: '', cnpj: '' });
    } else if (mode === 'edit' && unidade) {
      setFormUnidade({ nome: unidade.nome, codigo: unidade.codigo, cnpj: unidade.cnpj || '' });
    }
    setModalUnidade({ open: true, mode, unidade });
  };

  const handleCloseModalUnidade = () => {
    setModalUnidade({ open: false, mode: 'create', unidade: null });
    setFormUnidade({ nome: '', codigo: '', cnpj: '' });
  };

  const handleSalvarUnidade = async () => {
    if (!formUnidade.nome || !formUnidade.codigo) {
      setSnackbar({ open: true, message: 'Nome e código são obrigatórios', severity: 'error' });
      return;
    }
    try {
      if (modalUnidade.mode === 'create') {
        const res = await apiFetch('/api/unidades', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formUnidade)
        });
        if (!res.ok) throw new Error('Erro ao criar unidade');
        setSnackbar({ open: true, message: 'Unidade criada com sucesso!', severity: 'success' });
      } else {
        const res = await apiFetch(`/api/unidades/${modalUnidade.unidade.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...formUnidade, ativa: modalUnidade.unidade.ativa })
        });
        if (!res.ok) throw new Error('Erro ao atualizar unidade');
        setSnackbar({ open: true, message: 'Unidade atualizada com sucesso!', severity: 'success' });
      }
      handleCloseModalUnidade();
      fetchUnidades();
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Erro ao salvar unidade', severity: 'error' });
    }
  };

  const handleSalvarUnidadeLocal = async () => {
    const alvo = listaUnidades.find((u) => u.codigo === unidadeSelecionada?.codigo) || unidadeSelecionada;
    if (!alvo?.id && !alvo?.codigo) {
      setSnackbar({ open: true, message: 'Unidade não encontrada para edição.', severity: 'error' });
      return;
    }
    try {
      const id = alvo.id;
      if (!id) throw new Error('ID da unidade indisponível');
      const res = await apiFetch(`/api/unidades/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome: formUnidadeLocal.nome,
          codigo: formUnidadeLocal.codigo,
          cnpj: formUnidadeLocal.cnpj,
          ativa: alvo.ativa !== undefined ? alvo.ativa : 1
        })
      });
      if (!res.ok) throw new Error('Erro ao atualizar unidade');
      setSnackbar({ open: true, message: 'Dados da unidade salvos!', severity: 'success' });
      fetchUnidades();
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Erro ao salvar', severity: 'error' });
    }
  };

  const handleDeletarUnidade = (unidade) => {
    // Soft delete preferível — API atual usa DELETE; confirmar com usuário
    if (window.confirm(`Inativar/remover a unidade "${unidade.nome}"? Prefira soft delete no backend.`)) {
      apiFetch(`/api/unidades/${unidade.id}`, { method: 'DELETE' })
        .then((res) => {
          if (!res.ok) throw new Error('Erro ao deletar unidade');
          setSnackbar({ open: true, message: 'Unidade removida com sucesso!', severity: 'success' });
          fetchUnidades();
        })
        .catch((error) => {
          setSnackbar({ open: true, message: error.message || 'Erro ao deletar unidade', severity: 'error' });
        });
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const usuariosRes = await apiFetch(withUnidade('/api/usuarios-permissoes', unidadeSelecionada));
      const usuariosData = await usuariosRes.json();
      setUsuarios(usuariosData);

      try {
        const setoresRes = await apiFetch(withUnidade('/api/setores', unidadeSelecionada));
        if (setoresRes.ok) {
          const setoresData = await setoresRes.json();
          let nomesSetores = setoresData
            .filter((s) => s.ativo === undefined || s.ativo === 1)
            .map((s) => s.nome)
            .filter(Boolean);
          if (!nomesSetores.includes('Contratos')) nomesSetores.push('Contratos');
          setSetores(nomesSetores);
        } else {
          const setoresUnicos = [...new Set(usuariosData.map((u) => u.setor).filter(Boolean))];
          if (!setoresUnicos.includes('Contratos')) setoresUnicos.push('Contratos');
          setSetores(setoresUnicos);
        }
      } catch (e) {
        console.error('Erro ao buscar setores, usando fallback de usuários:', e);
        const setoresUnicos = [...new Set(usuariosData.map((u) => u.setor).filter(Boolean))];
        if (!setoresUnicos.includes('Contratos')) setoresUnicos.push('Contratos');
        setSetores(setoresUnicos);
      }

      const perms = {};
      usuariosData.forEach((u) => {
        perms[u.id] = {
          dashboard: u.dashboard === 1,
          financeiro: u.financeiro === 1,
          contratos: u.contratos === 1,
          fiscais: u.fiscais === 1,
          avaliacao: u.avaliacao === 1,
          regulamentos: u.regulamentos === 1,
          configuracao: u.configuracao === 1
        };
      });
      setPermissions(perms);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setSnackbar({ open: true, message: 'Erro ao carregar dados', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = (userId, permission, value) => {
    setPermissions((prev) => ({
      ...prev,
      [userId]: { ...prev[userId], [permission]: value }
    }));
  };

  const handleSetorChange = (userId, setor) => {
    setUsuarios((prev) => prev.map((u) => (u.id === userId ? { ...u, setor } : u)));
  };

  const handleTipoChange = (userId, tipo) => {
    setUsuarios((prev) => prev.map((u) => (u.id === userId ? { ...u, tipo } : u)));
  };

  const savePermissions = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(withUnidade('/api/usuarios-permissoes', unidadeSelecionada), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ usuarios })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setSnackbar({ open: true, message: 'Permissões salvas com sucesso!', severity: 'success' });
        await fetchData();
      } else {
        setSnackbar({ open: true, message: data.error || 'Erro ao salvar permissões', severity: 'error' });
      }
    } catch (error) {
      console.error('Erro ao salvar permissões:', error);
      setSnackbar({ open: true, message: 'Erro de conexão com o servidor', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setConfig({ ...config, [field]: value });
  };

  const handleSave = async () => {
    try {
      setUnitStorageJSON('systemConfig', unidadeSelecionada?.codigo, config);
      setSnackbar({ open: true, message: 'Configurações salvas com sucesso!', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Erro ao salvar configurações.', severity: 'error' });
    }
  };

  const handleBackup = async () => {
    try {
      const res = await apiFetch('/api/backup');
      if (!res.ok) throw new Error('Erro ao gerar backup');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setSnackbar({ open: true, message: 'Backup gerado com sucesso!', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Erro ao gerar backup.', severity: 'error' });
    }
  };

  const handleRestore = () => {
    setSnackbar({ open: true, message: 'Funcionalidade de restauração em desenvolvimento.', severity: 'info' });
  };

  const contratosFiltrados = useMemo(() => {
    return contratosComm.filter((c) => {
      if (filtroSetor && c.setor !== filtroSetor) return false;
      if (filtroFiscal) {
        const f = fiscaisComm.find((x) => x.setor === c.setor);
        if (!f || String(f.id) !== String(filtroFiscal)) return false;
      }
      if (filtroVencimento === 'proximo') {
        const d = diasParaVencimento(c.vigenciaFim);
        if (d === null || d > 90) return false;
      }
      return true;
    });
  }, [contratosComm, filtroSetor, filtroFiscal, filtroVencimento, fiscaisComm]);

  const prestadoresPortalFiltrados = useMemo(() => {
    return prestadoresPortal.filter((c) => {
      if (filtroSetorPortal && c.setor !== filtroSetorPortal) return false;
      if (filtroFiscalPortal) {
        const f = fiscaisComm.find((x) => x.setor === c.setor);
        if (!f || String(f.id) !== String(filtroFiscalPortal)) return false;
      }
      if (filtroVencimentoPortal === 'proximo') {
        const d = diasParaVencimento(c.vigenciaFim);
        if (d === null || d > 90) return false;
      }
      return true;
    });
  }, [prestadoresPortal, filtroSetorPortal, filtroFiscalPortal, filtroVencimentoPortal, fiscaisComm]);

  const idsFiltrados = useMemo(
    () => contratosFiltrados.map((c) => c.id || c.numeroContrato),
    [contratosFiltrados]
  );

  const todosSelecionados =
    idsFiltrados.length > 0 && idsFiltrados.every((id) => selectedIds.includes(id));

  const toggleSelectAll = () => {
    if (todosSelecionados) {
      setSelectedIds((prev) => prev.filter((id) => !idsFiltrados.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...idsFiltrados])));
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const fiscalDoContrato = (contrato) => {
    const setor = contrato.setor;
    return fiscaisComm.find((x) => x.setor === setor) || null;
  };

  const abrirEmailModal = (tipo, contratosInput) => {
    const lista = Array.isArray(contratosInput) ? contratosInput : [contratosInput];
    const comFiscal = lista.map((c) => ({ ...c, _fiscal: fiscalDoContrato(c) }));
    const empresaExemplo = comFiscal[0]?.empresa || 'Prestador';
    setEmailModal({
      open: true,
      tipo,
      contratos: comFiscal,
      corpo: EMAIL_TEMPLATES[tipo](empresaExemplo).replace(
        empresaExemplo,
        comFiscal.length > 1 ? '[NOME DA EMPRESA]' : empresaExemplo
      ),
      copiaContratos: true,
      enviando: false
    });
  };

  const abrirEmailMassa = (tipo) => {
    const selecionados = contratosFiltrados.filter((c) =>
      selectedIds.includes(c.id || c.numeroContrato)
    );
    if (!selecionados.length) {
      setSnackbar({ open: true, message: 'Selecione ao menos um contrato.', severity: 'warning' });
      return;
    }
    abrirEmailModal(tipo, selecionados);
  };

  const handleDispararEmail = async () => {
    const { tipo, contratos, corpo, copiaContratos } = emailModal;
    if (!contratos?.length) {
      setSnackbar({ open: true, message: 'Nenhum destinatário selecionado.', severity: 'warning' });
      return;
    }

    const semEmail = contratos.filter((c) => !c.email);
    if (semEmail.length === contratos.length) {
      setSnackbar({
        open: true,
        message: 'Nenhum prestador selecionado tem e-mail cadastrado.',
        severity: 'warning'
      });
      return;
    }

    const payload = {
      tipoAcao: tipo === 'nf' ? 'SOLICITAR_NF' : 'SOLICITAR_RENOVACAO',
      unidade: unidadeSelecionada?.codigo,
      massa: contratos.length > 1,
      itens: contratos.map((c) => ({
        contratoId: c.id,
        numeroContrato: c.numeroContrato,
        empresa: c.empresa,
        para: c.email || null,
        cc: c._fiscal?.email || null,
        corpo: corpo.includes('[NOME DA EMPRESA]')
          ? corpo.replace(/\[NOME DA EMPRESA\]/g, c.empresa || 'Prestador')
          : corpo
      })),
      copiaSetorContratos: copiaContratos,
      disparadoPor: user?.email,
      disparadoEm: new Date().toISOString()
    };

    setEmailModal((m) => ({ ...m, enviando: true }));
    try {
      const res = await apiFetch(withUnidade('/api/notificacoes/disparar-massa', unidadeSelecionada), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao disparar e-mail');

      const msg =
        data.message ||
        `${data.enviados || 0} e-mail(s) enviado(s).`;
      setSnackbar({
        open: true,
        message: msg,
        severity: data.enviados > 0 ? (data.falhas ? 'warning' : 'success') : 'warning'
      });
      setEmailModal({ open: false, tipo: null, contratos: [], corpo: '', copiaContratos: true, enviando: false });
      setSelectedIds([]);
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Erro ao disparar e-mail', severity: 'error' });
      setEmailModal((m) => ({ ...m, enviando: false }));
    }
  };

  const salvarEmailPrestador = async () => {
    const p = emailEditModal.prestador;
    if (!p?.id) return;
    try {
      const res = await apiFetch(withUnidade(`/api/prestadores/${p.id}/email`, unidadeSelecionada), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailEditModal.email })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao salvar e-mail');
      const emailSalvo = data.email || emailEditModal.email;
      const cnpjNorm = String(p.cnpj || '').replace(/\D/g, '');
      setContratosComm((prev) =>
        prev.map((x) => {
          const sameCnpj = cnpjNorm && String(x.cnpj || '').replace(/\D/g, '') === cnpjNorm;
          return sameCnpj || x.id === p.id ? { ...x, email: emailSalvo } : x;
        })
      );
      setPrestadoresPortal((prev) =>
        prev.map((x) => {
          const sameCnpj = cnpjNorm && String(x.cnpj || '').replace(/\D/g, '') === cnpjNorm;
          return sameCnpj || x.id === p.id ? { ...x, email: emailSalvo } : x;
        })
      );
      setSnackbar({
        open: true,
        message:
          data.message ||
          `E-mail aplicado a ${data.contratosAtualizados || 1} contrato(s). Login portal: senha 123456.`,
        severity: 'success'
      });
      setEmailEditModal({ open: false, prestador: null, email: '' });
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    }
  };

  const inativarPrestador = async () => {
    const p = confirmInativarPrest;
    if (!p?.id) return;
    try {
      const res = await apiFetch(withUnidade(`/api/prestadores/${p.id}/inativar`, unidadeSelecionada), {
        method: 'PATCH'
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao inativar');
      setPrestadoresPortal((prev) => prev.filter((x) => x.id !== p.id));
      setSnackbar({ open: true, message: data.message || 'Prestador inativado.', severity: 'success' });
      setConfirmInativarPrest(null);
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    }
  };

  /**
   * God Mode — impersonation real (token em sessoes_prestador).
   */
  const handleGodMode = async (prestador) => {
    try {
      if (!prestador?.email) {
        setSnackbar({
          open: true,
          message: 'Cadastre o e-mail do prestador antes de acessar o portal.',
          severity: 'warning'
        });
        return;
      }
      const res = await apiFetch(withUnidade('/api/admin/prestador/impersonate', unidadeSelecionada), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prestadorId: prestador.id,
          cnpj: prestador.cnpj,
          email: prestador.email,
          empresa: prestador.empresa,
          unidade: unidadeSelecionada?.codigo
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao acessar portal do prestador');

      setPrestadorSession(data.token, {
        ...data.prestador,
        godMode: true,
        impersonadoPor: user?.email
      });
      if (Array.isArray(data.contratos)) {
        sessionStorage.setItem('godModeContratosSeed', JSON.stringify(data.contratos));
      }
      localStorage.setItem(
        'godModeAudit',
        JSON.stringify({
          by: user?.email,
          byNome: user?.nome,
          prestadorId: data.prestador?.id || prestador.id,
          empresa: data.prestador?.razaoSocial || prestador.empresa,
          at: new Date().toISOString(),
          unidade: unidadeSelecionada?.codigo,
          contratos: Array.isArray(data.contratos) ? data.contratos.length : null
        })
      );
      setSnackbar({
        open: true,
        message: `Acessando portal como ${data.prestador?.razaoSocial || prestador.empresa}…`,
        severity: 'info'
      });
      navigate('/prestador/dashboard');
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Erro no God Mode', severity: 'error' });
    }
  };

  // Índices de abas dinâmicos (Config Globais só SUPER_ADMIN)
  const tabsMeta = useMemo(() => {
    const list = [
      { key: 'unidade', label: 'Dados da Unidade', icon: <BusinessIcon fontSize="small" /> },
      ...(isSuperAdmin
        ? [{ key: 'globais', label: 'Configurações Globais', icon: <SettingsIcon fontSize="small" /> }]
        : []),
      { key: 'comunicacoes', label: 'Central de Comunicações', icon: <CampaignIcon fontSize="small" /> },
      { key: 'portal', label: 'Portal do Prestador / Editais', icon: <StorefrontIcon fontSize="small" /> },
      { key: 'fluxos', label: 'Configuração de fluxos', icon: <AccountTreeIcon fontSize="small" /> }
    ];
    return list;
  }, [isSuperAdmin]);

  const tabKey = tabsMeta[tab]?.key || 'unidade';

  if (!canAccessConfiguracao()) return <Navigate to="/home" replace />;

  return (
    <>
      <Box mb={3}>
        <Typography variant="h5" fontWeight={700}>
          Centro de Comando e Automação
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Unidade: <strong>{unidadeSelecionada?.nome || '—'}</strong>
          {isGlobalUser?.() ? ' · Visão global' : ''}
          {isLocalAdmin && !isSuperAdmin ? ' · LOCAL_ADMIN' : ''}
          {isSuperAdmin ? ' · SUPER_ADMIN' : ''}
        </Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 1,
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTab-root': { fontWeight: 600, textTransform: 'none' }
        }}
      >
        {tabsMeta.map((t) => (
          <Tab key={t.key} icon={t.icon} iconPosition="start" label={t.label} />
        ))}
      </Tabs>

      {/* ========== ABA 1: Dados da Unidade ========== */}
      {tabKey === 'unidade' && (
        <TabPanel value={0} index={0}>
          <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center">
                  <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" fontWeight={600}>
                    {isSuperAdmin ? 'Gerenciar Unidades' : 'Dados da Unidade Atual'}
                  </Typography>
                </Box>
                {isSuperAdmin && (
                  <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenModalUnidade('create')}>
                    Nova Unidade
                  </Button>
                )}
              </Box>

              {!isSuperAdmin && (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Nome"
                      fullWidth
                      value={formUnidadeLocal.nome}
                      onChange={(e) => setFormUnidadeLocal({ ...formUnidadeLocal, nome: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField label="Código" fullWidth value={formUnidadeLocal.codigo} disabled />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="CNPJ"
                      fullWidth
                      value={formUnidadeLocal.cnpj}
                      onChange={(e) => setFormUnidadeLocal({ ...formUnidadeLocal, cnpj: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSalvarUnidadeLocal}>
                      Salvar dados da unidade
                    </Button>
                  </Grid>
                </Grid>
              )}

              {isSuperAdmin && (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Nome</strong></TableCell>
                        <TableCell><strong>Código</strong></TableCell>
                        <TableCell><strong>CNPJ</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell align="right"><strong>Ações</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {listaUnidades.map((unidade) => (
                        <TableRow key={unidade.id} selected={unidade.codigo === unidadeSelecionada?.codigo}>
                          <TableCell>{unidade.nome}</TableCell>
                          <TableCell>{unidade.codigo}</TableCell>
                          <TableCell>{unidade.cnpj || 'Não informado'}</TableCell>
                          <TableCell>
                            <Chip
                              label={unidade.ativa ? 'Ativa' : 'Inativa'}
                              color={unidade.ativa ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Editar Unidade">
                              <IconButton size="small" color="primary" onClick={() => handleOpenModalUnidade('edit', unidade)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remover Unidade">
                              <IconButton size="small" color="error" onClick={() => handleDeletarUnidade(unidade)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                      {listaUnidades.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <Typography variant="body2" color="text.secondary">Nenhuma unidade cadastrada</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </TabPanel>
      )}

      {/* ========== ABA 2: Configurações Globais (SUPER_ADMIN) ========== */}
      {tabKey === 'globais' && isSuperAdmin && (
        <Box sx={{ pt: 3 }}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center">
                  <SecurityIcon sx={{ mr: 1 }} />
                  <Typography variant="h6" fontWeight={600}>Sistema de Permissões</Typography>
                </Box>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={savePermissions} disabled={loading}>
                  Salvar Permissões
                </Button>
              </Box>

              <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Usuário</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Setor</TableCell>
                      <TableCell>Dashboard</TableCell>
                      <TableCell>Financeiro</TableCell>
                      <TableCell>Contratos</TableCell>
                      <TableCell>Fiscais</TableCell>
                      <TableCell>Avaliação</TableCell>
                      <TableCell>Regulamentos</TableCell>
                      <TableCell>Configuração</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {usuarios.map((u) => (
                      <TableRow key={u.id}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{u.nome || u.email}</Typography>
                            <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <Select value={u.tipo || 'usuario'} onChange={(e) => handleTipoChange(u.id, e.target.value)}>
                              <MenuItem value="usuario">Usuário</MenuItem>
                              <MenuItem value="fiscal">Fiscal</MenuItem>
                              <MenuItem value="financeiro">Financeiro</MenuItem>
                              <MenuItem value="admin">Administrador</MenuItem>
                              <MenuItem value="diretor_presidente">Diretor Presidente</MenuItem>
                              <MenuItem value="diretor_financeiro">Diretor Financeiro</MenuItem>
                              <MenuItem value="diretor_tecnico">Diretor Técnico</MenuItem>
                              <MenuItem value="diretoria_executiva">Diretoria Executiva</MenuItem>
                              <MenuItem value="gerente_corporativo">Gerente Corporativo</MenuItem>
                              <MenuItem value="coordenador">Coordenador</MenuItem>
                              <MenuItem value="supervisor">Supervisor</MenuItem>
                              <MenuItem value="analista">Analista</MenuItem>
                              <MenuItem value="assistente">Assistente</MenuItem>
                              <MenuItem value="auxiliar">Auxiliar</MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          {u.tipo === 'fiscal' ? (
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <Select value={u.setor || ''} onChange={(e) => handleSetorChange(u.id, e.target.value)}>
                                {setores.map((setor) => (
                                  <MenuItem key={setor} value={setor}>{setor}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : (
                            <Chip label="N/A" size="small" color="default" />
                          )}
                        </TableCell>
                        {['dashboard', 'financeiro', 'contratos', 'fiscais', 'avaliacao', 'regulamentos', 'configuracao'].map((perm) => (
                          <TableCell key={perm}>
                            <Switch
                              checked={permissions[u.id]?.[perm] || false}
                              onChange={(e) => handlePermissionChange(u.id, perm, e.target.checked)}
                              size="small"
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <SecurityIcon sx={{ mr: 1 }} />
                <Typography variant="h6" fontWeight={600}>Configurações Gerais</Typography>
              </Box>
              <TextField
                label="URL do Backend"
                value={config.backendUrl}
                onChange={handleChange('backendUrl')}
                fullWidth
                margin="normal"
                helperText="URL da API do sistema"
              />
              <FormControlLabel
                control={<Switch checked={config.darkMode} onChange={handleChange('darkMode')} />}
                label="Modo Escuro"
                sx={{ mt: 1 }}
              />
              <FormControlLabel
                control={<Switch checked={config.compactMode} onChange={handleChange('compactMode')} />}
                label="Interface Compacta"
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <NotificationsIcon sx={{ mr: 1 }} />
                <Typography variant="h6" fontWeight={600}>Alertas e Notificações</Typography>
              </Box>
              <TextField
                label="Dias de Antecedência para Alertas"
                type="number"
                value={config.alertDays}
                onChange={handleChange('alertDays')}
                fullWidth
                margin="normal"
                helperText="Dias antes do vencimento para alertar sobre contratos"
              />
              <FormControlLabel
                control={<Switch checked={config.emailNotifications} onChange={handleChange('emailNotifications')} />}
                label="Notificações por Email"
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <BackupIcon sx={{ mr: 1 }} />
                <Typography variant="h6" fontWeight={600}>Backup e Restauração</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Faça backup dos dados do sistema ou restaure de um backup anterior.
              </Typography>
              <Box display="flex" gap={2}>
                <Button variant="outlined" startIcon={<BackupIcon />} onClick={handleBackup}>Gerar Backup</Button>
                <Button variant="outlined" startIcon={<RestoreIcon />} onClick={handleRestore}>Restaurar Backup</Button>
              </Box>
            </CardContent>
          </Card>

          <Box display="flex" justifyContent="flex-end" mt={3}>
            <Button variant="contained" color="primary" startIcon={<SaveIcon />} onClick={handleSave} size="large">
              Salvar Configurações
            </Button>
          </Box>
        </Box>
      )}

      {/* ========== ABA 3: Central de Comunicações ========== */}
      {tabKey === 'comunicacoes' && (
        <Box sx={{ pt: 3 }}>
          <Card sx={{ mb: 3, borderRadius: 3 }}>
            <CardContent>
              <Box display="flex" flexWrap="wrap" alignItems="center" justifyContent="space-between" gap={1} mb={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <EmailIcon color="primary" />
                  <Typography variant="h6" fontWeight={700}>Central de Comunicações</Typography>
                </Box>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ReceiptIcon />}
                    disabled={!selectedIds.length}
                    onClick={() => abrirEmailMassa('nf')}
                  >
                    NF aos selecionados ({selectedIds.length})
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    startIcon={<DescriptionIcon />}
                    disabled={!selectedIds.length}
                    onClick={() => abrirEmailMassa('renovacao')}
                  >
                    Renovação aos selecionados
                  </Button>
                  <Button size="small" onClick={() => { setSelectedIds(idsFiltrados); }}>
                    Selecionar todos filtrados
                  </Button>
                </Box>
              </Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Setor</InputLabel>
                    <Select label="Setor" value={filtroSetor} onChange={(e) => setFiltroSetor(e.target.value)}>
                      <MenuItem value="">Todos</MenuItem>
                      {setores.map((s) => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Fiscal</InputLabel>
                    <Select label="Fiscal" value={filtroFiscal} onChange={(e) => setFiltroFiscal(e.target.value)}>
                      <MenuItem value="">Todos</MenuItem>
                      {fiscaisComm.map((f) => (
                        <MenuItem key={f.id} value={String(f.id)}>{f.nome} ({f.setor})</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Vencimento</InputLabel>
                    <Select label="Vencimento" value={filtroVencimento} onChange={(e) => setFiltroVencimento(e.target.value)}>
                      <MenuItem value="todos">Todos</MenuItem>
                      <MenuItem value="proximo">Vencimento próximo (≤ 90 dias)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {loadingComm ? (
                <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
              ) : (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={todosSelecionados}
                            indeterminate={selectedIds.some((id) => idsFiltrados.includes(id)) && !todosSelecionados}
                            onChange={toggleSelectAll}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Empresa</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Contrato</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Setor</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Fiscal</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>E-mail</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Ações Rápidas</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {contratosFiltrados.map((c) => {
                        const fiscal = fiscalDoContrato(c);
                        const rowId = c.id || c.numeroContrato;
                        return (
                          <TableRow key={rowId} hover selected={selectedIds.includes(rowId)}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedIds.includes(rowId)}
                                onChange={() => toggleSelectOne(rowId)}
                              />
                            </TableCell>
                            <TableCell>{c.empresa}</TableCell>
                            <TableCell>{c.numeroContrato}</TableCell>
                            <TableCell>{c.setor || '—'}</TableCell>
                            <TableCell>{fiscal?.nome || '—'}</TableCell>
                            <TableCell>
                              <Typography variant="caption">{c.email || '—'}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Solicitar NF (só este)">
                                <IconButton color="primary" onClick={() => abrirEmailModal('nf', c)}>
                                  <ReceiptIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Solicitar Renovação (só este)">
                                <IconButton color="secondary" onClick={() => abrirEmailModal('renovacao', c)}>
                                  <DescriptionIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {contratosFiltrados.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} align="center">Nenhum contrato encontrado com os filtros.</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* ========== ABA: Configuração de fluxos ========== */}
      {tabKey === 'fluxos' && (
        <Box sx={{ pt: 3 }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Configuração de fluxos
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Contratação, Aditivo, Minuta, Pagamento e templates (TR, CI, Notificação) em um só lugar.
          </Typography>
          <FluxosConfigPanel
            podeEditar={Boolean(
              isSuperAdmin ||
                isLocalAdmin ||
                isAdmin ||
                String(user?.setor || '').toLowerCase().includes('contrato')
            )}
          />
        </Box>
      )}

      {/* ========== ABA 4: Portal / Editais ========== */}
      {tabKey === 'portal' && (
        <Box sx={{ pt: 3 }}>
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            Os editais agora são sincronizados automaticamente a partir da Etapa &quot;Edital&quot; do fluxo de processos da unidade.
          </Alert>

          <Card sx={{ mb: 3, borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Prestadores — E-mail, God Mode e exclusão
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Ao salvar o e-mail, ele é replicado a todos os contratos do mesmo CNPJ, cria a solicitação
                de cadastro e o login do portal (senha padrão <strong>123456</strong>).
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Setor</InputLabel>
                    <Select
                      label="Setor"
                      value={filtroSetorPortal}
                      onChange={(e) => setFiltroSetorPortal(e.target.value)}
                    >
                      <MenuItem value="">Todos</MenuItem>
                      {setores.map((s) => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Fiscal</InputLabel>
                    <Select
                      label="Fiscal"
                      value={filtroFiscalPortal}
                      onChange={(e) => setFiltroFiscalPortal(e.target.value)}
                    >
                      <MenuItem value="">Todos</MenuItem>
                      {fiscaisComm.map((f) => (
                        <MenuItem key={f.id} value={String(f.id)}>{f.nome} ({f.setor})</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Vencimento</InputLabel>
                    <Select
                      label="Vencimento"
                      value={filtroVencimentoPortal}
                      onChange={(e) => setFiltroVencimentoPortal(e.target.value)}
                    >
                      <MenuItem value="todos">Todos</MenuItem>
                      <MenuItem value="proximo">Vencimento próximo (≤ 90 dias)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Empresa</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>CNPJ</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Contrato</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Setor</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>E-mail</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {prestadoresPortalFiltrados.slice(0, 100).map((p) => (
                      <TableRow key={p.id} hover>
                        <TableCell>{p.empresa}</TableCell>
                        <TableCell>{p.cnpj || '—'}</TableCell>
                        <TableCell>{p.numeroContrato}</TableCell>
                        <TableCell>{p.setor || '—'}</TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            sx={{ textTransform: 'none', justifyContent: 'flex-start' }}
                            onClick={() => setEmailEditModal({ open: true, prestador: p, email: p.email || '' })}
                          >
                            {p.email || 'Adicionar e-mail'}
                          </Button>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Editar e-mail">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => setEmailEditModal({ open: true, prestador: p, email: p.email || '' })}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Acessar como Prestador">
                            <IconButton color="primary" onClick={() => handleGodMode(p)}>
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Inativar prestador">
                            <IconButton color="error" onClick={() => setConfirmInativarPrest(p)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    {prestadoresPortalFiltrados.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">Nenhum prestador com os filtros selecionados.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight={700} color="primary">
                Editais e Solicitações de Cadastro
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Cancelar edital, editar e-mail/senha e inativar prestadores nas solicitações.
              </Typography>
              <PortalEditaisDocsConfigPanel setSnackbar={setSnackbar} />
              <GavConfigPanel setSnackbar={setSnackbar} />
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Modal Criar/Editar Unidade */}
      <Dialog open={modalUnidade.open} onClose={handleCloseModalUnidade} maxWidth="sm" fullWidth>
        <DialogTitle>{modalUnidade.mode === 'create' ? 'Criar Nova Unidade' : 'Editar Unidade'}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            {modalUnidade.mode === 'create'
              ? 'Preencha os campos abaixo para criar uma nova unidade.'
              : 'Edite os campos abaixo para atualizar a unidade.'}
          </DialogContentText>
          <TextField
            label="Nome da Unidade"
            value={formUnidade.nome}
            onChange={(e) => setFormUnidade({ ...formUnidade, nome: e.target.value })}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Código da Unidade"
            value={formUnidade.codigo}
            onChange={(e) => setFormUnidade({ ...formUnidade, codigo: e.target.value })}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="CNPJ"
            value={formUnidade.cnpj}
            onChange={(e) => setFormUnidade({ ...formUnidade, cnpj: e.target.value })}
            fullWidth
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModalUnidade}>Cancelar</Button>
          <Button onClick={handleSalvarUnidade} variant="contained">
            {modalUnidade.mode === 'create' ? 'Criar' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Disparo de E-mail (individual ou massa) */}
      <Dialog
        open={emailModal.open}
        onClose={() => !emailModal.enviando && setEmailModal((m) => ({ ...m, open: false }))}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {emailModal.tipo === 'nf' ? 'Solicitar Nota Fiscal' : 'Solicitar Renovação'}
          {emailModal.contratos?.length === 1
            ? ` — ${emailModal.contratos[0]?.empresa || ''}`
            : emailModal.contratos?.length > 1
              ? ` — ${emailModal.contratos.length} destinatários`
              : ''}
        </DialogTitle>
        <DialogContent dividers>
          {emailModal.contratos?.length === 1 ? (
            <>
              <TextField
                label="Para (Prestador)"
                fullWidth
                sx={{ mb: 2 }}
                value={
                  emailModal.contratos[0]?.email
                  || emailModal.contratos[0]?.emailPrestador
                  || 'E-mail do prestador (quando cadastrado)'
                }
                disabled
              />
              <TextField
                label="CC (Fiscal)"
                fullWidth
                sx={{ mb: 2 }}
                value={emailModal.contratos[0]?._fiscal?.email || 'Fiscal sem e-mail cadastrado'}
                disabled
              />
            </>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              Serão disparados {emailModal.contratos?.length || 0} e-mails individuais:
              cada um para o prestador, com CC do fiscal do setor.
              Use <code>[NOME DA EMPRESA]</code> no corpo para personalizar.
            </Alert>
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={emailModal.copiaContratos}
                onChange={(e) => setEmailModal((m) => ({ ...m, copiaContratos: e.target.checked }))}
              />
            }
            label="Enviar cópia para o Setor de Contratos"
            sx={{ mb: 2 }}
          />
          <TextField
            label="Corpo do e-mail"
            fullWidth
            multiline
            minRows={8}
            value={emailModal.corpo}
            onChange={(e) => setEmailModal((m) => ({ ...m, corpo: e.target.value }))}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button disabled={emailModal.enviando} onClick={() => setEmailModal((m) => ({ ...m, open: false }))}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={emailModal.enviando ? <CircularProgress size={18} color="inherit" /> : <EmailIcon />}
            disabled={emailModal.enviando || !emailModal.contratos?.length}
            onClick={handleDispararEmail}
          >
            Disparar {emailModal.contratos?.length > 1 ? `${emailModal.contratos.length} e-mails` : 'E-mail'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Editar e-mail do prestador */}
      <Dialog
        open={emailEditModal.open}
        onClose={() => setEmailEditModal({ open: false, prestador: null, email: '' })}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          E-mail — {emailEditModal.prestador?.empresa || 'Prestador'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            O e-mail será aplicado a todos os contratos do mesmo CNPJ, com login no portal (senha <strong>123456</strong>).
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="E-mail do prestador"
            type="email"
            margin="dense"
            value={emailEditModal.email}
            onChange={(e) => setEmailEditModal((m) => ({ ...m, email: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailEditModal({ open: false, prestador: null, email: '' })}>Cancelar</Button>
          <Button variant="contained" onClick={salvarEmailPrestador}>Salvar</Button>
        </DialogActions>
      </Dialog>

      {/* Confirmar inativação do prestador */}
      <Dialog open={Boolean(confirmInativarPrest)} onClose={() => setConfirmInativarPrest(null)}>
        <DialogTitle>Inativar prestador?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Soft delete de <strong>{confirmInativarPrest?.empresa}</strong> (contrato{' '}
            {confirmInativarPrest?.numeroContrato}). O registro permanece no histórico.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmInativarPrest(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={inativarPrestador}>Inativar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
